import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { IPayload } from "../interface/filters";
import { IactionTitleChart } from "../interface/Info-text-analitycs";
import { NpsTrendByQuartile } from "../modules/pages/analysis/sales-analysis/nps-trend-by-quartile";
import { CommentCategory } from "../widgets/charts/half-chart/comment-category";
import { DataQualityInfo, DataQualityResponse } from "../widgets/data-quality-panel/data-quality-info";
import { RejectReason } from "../widgets/fieldwork-lines-doughnut/reject-reason";
import { FieldworkReportSummary } from "../widgets/fieldwork-panel-filters/fieldwork-report-status";
import { NsatByMarketAndSupplier } from "../widgets/supplier-performance/supplier-performance.component";
import { ChartDataResponse } from "../widgets/supplier-performance-trend/supplier-performance-trend.component";
import { ApplicationService } from "./application.service";
import { Router } from "@angular/router";
import { NSATResponse } from "./models/nsat-response";
import { HttpCacheService } from "./http-cache.service";
import { FlagUniqueService } from "./flag-unique.service";

export interface Nps {
  excellent: number | null;
  good: number | null;
  unacceptable: number | null;
  detractors: number | null;
  detractorsCount: number | null;
  diff: number | null;
  neutrals: number | null;
  neutralsCount: number | null;
  promoters: number | null;
  promotersCount: number | null;
  questioncode: number | null;
  score: number | null;
  totSurveys: number | null;
  yymm: string | null;
}
export interface NpsResponse {
  currentNPS: Nps,
  previousNPS: Nps,
  target: number,
}

export interface CurrentNpsOnRep {
  deltaPercentage: number | null;
  questionPercentage: number | null;
  responses: number | null;
  score: number | null;
  totPercentage: number | null;
}

export interface BrandCompetitorsTrendResponse {
  data: { scoreList: string[] }[
  ]
}

export interface MappedBrandCompetitorsTrendResponse {
  data: { score: string[] }[
  ]
}

export function emptyNps(overrides: Partial<Nps> = {}): Nps {
  return {
    excellent: null,
    good: null,
    unacceptable: null,
    detractors: null,
    detractorsCount: null,
    diff: null,
    neutrals: null,
    neutralsCount: null,
    promoters: null,
    promotersCount: null,
    questioncode: null,
    score: null,
    totSurveys: null,
    yymm: null,
    ...overrides
  };
}

// empty Nps showing zeros instead of hiding the values
export function zeroNps(overrides: Partial<Nps> = {}): Nps {
  return emptyNps({
    excellent: 0,
    good: 0,
    unacceptable: 0,
    detractors: 0,
    detractorsCount: 0,
    neutrals: 0,
    neutralsCount: 0,
    promoters: 0,
    promotersCount: 0,
    score: 0,
    totSurveys: 0,
    ...overrides
  });
}

@Injectable({
  providedIn: "root",
})
export class WidgetsService implements OnDestroy {
  public currentNPSWithTarget: BehaviorSubject<any>;
  public onlineReputationTarget: BehaviorSubject<any>;
  public NPSTrend: BehaviorSubject<any>;

  url = environment.csibefrontend;

  // Prefisso per la cache NPS
  private readonly NPS_CACHE_PREFIX = 'nps_cache_';

  constructor(
    private http: HttpClient,
    private applicationService: ApplicationService,
    private router: Router,
    private cacheService: HttpCacheService,
    private flagUniqueService: FlagUniqueService
  ) {
    this.currentNPSWithTarget = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("currentNPSWithTarget")!)
    );
    this.onlineReputationTarget = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem("onlineReputationTarget")!)
    );
    this.NPSTrend = new BehaviorSubject<any>(JSON.parse(localStorage.getItem("NPSTrend")!));

    this.cacheService.registerCache({
      prefix: this.NPS_CACHE_PREFIX,
      maxAge: 5 * 60 * 1000, // 4 minuti
      cleanupInterval: 5 * 60 * 1000 // 5 minuti
    });
  }

  ngOnDestroy(): void { }

  public setCurrentNPSWithTarget(currentNPSWithTargetSettings: any) {
    localStorage.setItem("currentNPSWithTarget", JSON.stringify(currentNPSWithTargetSettings));
    this.currentNPSWithTarget.next(currentNPSWithTargetSettings);
  }
  public setNPSTrend(NPSTrendSettings: any) {
    localStorage.setItem("NPSTrend", JSON.stringify(NPSTrendSettings));
    this.NPSTrend.next(<any>NPSTrendSettings);
  }

  getCurrentNPSWithTarget2(body: any) {
    this.setCurrentNPSWithTarget({
      loader: true,
      data: null,
      state: "waiting",
    });

    return this.http
      .post<{} | any[]>(this.url + `currentNPSWithTarget`, body)
      .subscribe((data: any) => {
        this.setCurrentNPSWithTarget({
          loader: false,
          data: data,
          state: data ? "completed" : "error",
        });
      });
  }

  getCurrentNPSWithTarget(body: any): Observable<NpsResponse> {
    // Section sempre uppercase, la tab non è rilevante per questa chiamata
    body = {
      ...body,
      question: {
        ...body.question,
        section: body.question.section.toUpperCase(),
        tab: undefined
      }
    };

    return this.cacheService.cachedRequest<NpsResponse>(
      this.NPS_CACHE_PREFIX,
      body,
      () => {
        let urlCurrentNPS: string;
        if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
          urlCurrentNPS = "v3/currentNPSWithTarget";
        } else {
          urlCurrentNPS = "v5/currentNPSWithTarget";
        }

        return this.http
          .post<NpsResponse>(this.url + urlCurrentNPS, body)
          .pipe(
            tap(data => {
              // rounds the target value to 1-decimal
              data.target = Math.round((data.target || 0) * 10) / 10;
            }),
            tap((data) => {
              this.setCurrentNPSWithTarget({
                loader: false,
                data: data,
                state: data ? "completed" : "error",
              });
            })
          );
      }
    ).pipe(
      tap((data) => {
        this.setCurrentNPSWithTarget({
          loader: false,
          data: data,
          state: data ? "completed" : "error",
        });
      })
    );
  }
  getCurrentNSATBeforeAfterRescoring(body: any, isBefore: boolean): Observable<NSATResponse> {
    return this.http
      .post<NSATResponse>(this.url + `v3/getCurrentNSATBeforeAfterRescoring?isBefore=` + isBefore, body)
      .pipe(
        catchError((err) => {
          return of({
            errors: '',
            current: undefined,
            previous: undefined,
            target: 0,
          });
        })
      );
  }

  public setOnlineReputationTarget(OnlineReputationTargetSettings: any) {
    localStorage.setItem("onlineReputationTarget", JSON.stringify(OnlineReputationTargetSettings));
    this.onlineReputationTarget.next(OnlineReputationTargetSettings);
  }

  getOnlineReputationTarget(body: any): Observable<NpsResponse> {
    return this.http
      .post<NpsResponse>(this.url + `v4/getOnlineReputationTarget`, body)
      .pipe(
        map((data): NpsResponse => ({
          currentNPS: data?.currentNPS ?? zeroNps(),
          previousNPS: data?.previousNPS ?? emptyNps(),
          // rounds the target value to 1-decimal
          target: Math.round((data?.target || 0) * 10) / 10,
        })),
        tap((data) => this.setOnlineReputationTarget({
          loader: false,
          data: data,
          state: "completed",
        })),
        catchError(() => {
          const fallback: NpsResponse = {
            currentNPS: zeroNps(),
            previousNPS: emptyNps(),
            target: 0,
          };
          this.setOnlineReputationTarget({
            loader: false,
            data: fallback,
            state: "error",
          });
          return of(fallback);
        })
      );
  }

  getNPSTrend(body: any) {
    return this.http
      .post<{
        data: { [key: string]: number[] };
        period: string[];
      }>(this.url + `v2/getNPSTrend`, body)
      .pipe(
        catchError((err) => {
          return of({
            data: {},
            period: [],
          });
        })
      );
  }

  getNewNpsTrend(payload: IPayload) {
    /*     return of({
      errors: null,
      target: null,
      periods: [
        "202108",
        "202109",
        "202110",
        "202111",
        "202112",
        "202201",
        "202202",
        "202203",
        "202204",
        "202205",
        "202206",
        "202207",
      ],
      brandNPS: null,
      multiBrandNPS: null,
      bestInClassMarket: null,
      bestInClassRegion: null,
      data: [
        {
          mainLabel: "brandNPS",
          brand: "b_CH",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: "MEA_KW_I226003",
          outlet: null,
          advisor: null,
          key: null,
          score: [null, 33.33, null, 28.57, 0, 87.5, 100, 33.33, null, null, null, null],
        },
        {
          mainLabel: "brandNPS",
          brand: "b_JP",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: "MEA_KW_I226003",
          outlet: null,
          advisor: null,
          key: null,
          score: [11.11, 21.05, -13.33, -16.67, null, 16.67, 47.67, 48.08, 40.0, 75.0, 83.33, 100],
        },
        {
          mainLabel: "brandNPS",
          brand: "b_FI",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: "MEA_KW_I226003",
          outlet: null,
          advisor: null,
          key: null,
          score: [null, null, null, null, null, 83.33, 42.86, 100, 0, -75.0, null, null],
        },
        {
          mainLabel: "multiBrandNPS",
          brand: null,
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: "MEA_KW_I226003",
          outlet: null,
          advisor: null,
          key: null,
          score: [11.11, 22.73, -12.5, -10.53, null, 26.53, 41.12, 52.46, 57.14, 54.84, 41.18, 100],
        },
        {
          mainLabel: "bestInClassMarket",
          brand: "b_CH",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: null,
          outlet: null,
          advisor: null,
          key: null,
          score: [100, 100, 33.33, 42.86, 27.27, 87.5, 100, 68.42, 76.92, 44.44, 71.43, null],
        },
        {
          mainLabel: "bestInClassMarket",
          brand: "b_JP",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: null,
          outlet: null,
          advisor: null,
          key: null,
          score: [11.11, 33.33, 20.0, 68.75, null, 66.67, 62.5, 72.0, 51.72, 75.0, 83.33, 100],
        },
        {
          mainLabel: "bestInClassMarket",
          brand: "b_FI",
          region: null,
          market: null,
          area: null,
          zone: null,
          group: null,
          dealer: null,
          outlet: null,
          advisor: null,
          key: null,
          score: [null, null, null, null, null, 83.33, 42.86, 100, 0, -75.0, null, null],
        },
      ],
      eeur: null,
    }); */

    //let myurl = this.url.replace("csibefrontend/", "");
    let urlNPSTrend: string;
    if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
      urlNPSTrend = "v4/getNPSTrend";
    } else {
      urlNPSTrend = "v5/getNPSTrend";
    }
    return this.http.post<any>(this.url + urlNPSTrend, payload).pipe(
      catchError((err) => {
        return of({
          periods: [],
          data: [],
        });
      })
    );
  }


  getNewNpsTrendOnlineReputation(payload: IPayload): Observable<MappedBrandCompetitorsTrendResponse> {
    // return of(
    //   {
    //     "errors": null,
    //     "target": 4.3,
    //     "periods": [
    //       "2022/12",
    //       "2023/01",
    //       "2023/02",
    //       "2023/03",
    //       "2023/04",
    //       "2023/05",
    //       "2023/06",
    //       "2023/07",
    //       "2023/08",
    //       "2023/09",
    //       "2023/10",
    //       "2023/11"
    //     ],
    //     "viewType": "REGION",
    //     "data": [
    //       {
    //         "mainLabel": "brandNPS",
    //         "region": "EUR",
    //         "subRegion": null,
    //         "market": null,
    //         "area": null,
    //         "zone": null,
    //         "group": null,
    //         "distributor": null,
    //         "dealer": null,
    //         "outlet": null,
    //         "advisor": null,
    //         "brand": "b_MASE",
    //         "key": null,
    //         "score": [
    //           4.1,
    //           3.8,
    //           4.5,
    //           4.5,
    //           4.2,
    //           4.0,
    //           4.1,
    //           4.4,
    //           3.8,
    //           4.6,
    //           4.3,
    //           4.2
    //         ]
    //       },
    //       {
    //         "mainLabel": "brandNPS",
    //         "region": "EUR",
    //         "subRegion": null,
    //         "market": null,
    //         "area": null,
    //         "zone": null,
    //         "group": null,
    //         "distributor": null,
    //         "dealer": null,
    //         "outlet": null,
    //         "advisor": null,
    //         "brand": "b_JP",
    //         "key": null,
    //         "score": [
    //           3.4,
    //           3.5,
    //           3.5,
    //           3.8,
    //           3.7,
    //           3.9,
    //           3.6,
    //           4.0,
    //           4.1,
    //           4.1,
    //           4.0,
    //           4,
    //           2
    //         ]
    //       }
    //     ]
    //   }
    // )


    return this.http.post<BrandCompetitorsTrendResponse>(this.url + `v1/getBrandCompetitorsTrend`, payload).pipe(
      map((result) => {
        let mappedData = result.data.map(el => {
          let mappedElement = {
            ...el,
            score: el.scoreList
          }
          return mappedElement;
        })
        let newResult: MappedBrandCompetitorsTrendResponse = { ...result, data: mappedData }
        return newResult
      }),
      catchError((err) => {
        return of({
          periods: [],
          data: [],
        });
      })
    )
  }

  getCategoriesPointsNPS(body: any) {
    return this.http
      .post<{
        bestCategories: any[];
        wrostCategories: any[];
      }>(this.url + `v4/getCategoriesPointsNPS`, body)
      .pipe(
        catchError((err) => {
          return of({
            bestCategories: [],
            wrostCategories: [],
          });
        })
      );
  }

  getTenStarTrend(body: any) {
    return this.http
      .post<{
        data: { [key: string]: number[] };
        period: string[];
      }>(this.url + `v5/getTenstarTrend`, body)
      .pipe(
        catchError((err) => {
          return of({
            data: {},
            period: [],
          });
        })
      );
  }

  getKeyQuestionsTrend(body: any) {
    let urlQuestionsTrend: string;
    if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
      urlQuestionsTrend = "v4/getKeyQuestionsTrend";
    } else {
      urlQuestionsTrend = "v5/getKeyQuestionsTrend";
    }
    return this.http
      .post<{
        data: { [key: string]: number[] };
        period: string[];
      }>(this.url + urlQuestionsTrend, body)
      .pipe(
        catchError((err) => {
          return of({
            data: {},
            period: [],
          });
        })
      );
  }

  getQuestionDonutChartData(body: any) {
    let urlQuestionDonut: string;
    if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
      urlQuestionDonut = "v4/getQuestionDonutChartData";
    } else {
      urlQuestionDonut = "v5/getQuestionDonutChartData";
    }
    return this.http
      .post<{
        currentNPS: Nps | undefined;
        previousNPS: Nps | undefined;
        target: number;
      }>(this.url + urlQuestionDonut, body)
      .pipe(
        catchError((err) => {
          let result: {
            currentNPS: Nps | undefined;
            previousNPS: Nps | undefined;
            target: number;
          } = {
            currentNPS: undefined,
            previousNPS: undefined,
            target: 0,
          };
          return of(result);
        })
      );
  }

  getMultiQuestionDonutChart(body: any) {
    let urlMultiQuestionDonut: string;
    if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
      urlMultiQuestionDonut = "v4/getMultiQuestionDonutChart";
    } else {
      urlMultiQuestionDonut = "v5/getMultiQuestionDonutChart";
    }
    return this.http
      .post<{
        currentNPS: Nps | undefined;
        previousNPS: Nps | undefined;
        target: number;
      }>(this.url + urlMultiQuestionDonut, body)
      .pipe(
        catchError((err) => {
          let result: {
            currentNPS: Nps | undefined;
            previousNPS: Nps | undefined;
            target: number;
          } = {
            currentNPS: undefined,
            previousNPS: undefined,
            target: 0,
          };
          return of(result);
        })
      )
  }
  getMultiQuestionDonutChartBeforeRescoring(body: any) {
    return this.http
      .post<{
        currentNPS: Nps | undefined;
        previousNPS: Nps | undefined;
        target: number;
      }>(this.url + `v3/getMultiQuestionDonutChartBeforeRescoring`, body)
      .pipe(
        catchError((err) => {
          let result: {
            currentNPS: Nps | undefined;
            previousNPS: Nps | undefined;
            target: number;
          } = {
            currentNPS: undefined,
            previousNPS: undefined,
            target: 0,
          };
          return of(result);
        })
      )
  }


  getMultiQuestionDonutChartOnRep(body: any) {
    return this.http
      .post<{
        currentNPS: Nps | undefined;
        previousNPS: Nps | undefined;
        currentOnRep: CurrentNpsOnRep | undefined;
        target: number;
      }>(this.url + `v3/getMultiQuestionDonutChart`, body)
      .pipe(
        catchError((err) => {
          let result: {
            currentNPS: Nps | undefined;
            previousNPS: Nps | undefined;
            currentOnRep: CurrentNpsOnRep | undefined;
            target: number;
          } = {
            currentOnRep: undefined,
            target: 0,
            currentNPS: undefined,
            previousNPS: undefined,
          };
          return of(result);
        })
      )
  }

  getNsatPerformanceByMarketAndSupplier(body: any): Observable<{ errors: any, suppliers: NsatByMarketAndSupplier[] | null }> {
    return this.http.post<{ errors: any, suppliers: NsatByMarketAndSupplier[] }>(this.url + `v3/getSupplierPerformance`, body)//.pipe(
    // catchError((err, obs) => of({ errors: null, suppliers: null }))
    //)
  }

  getNsatPerformanceTrendBySupplier(body: any): Observable<ChartDataResponse> {
    return this.http.post<ChartDataResponse>(this.url + `v3/getSupplierPerformanceTrend`, body)
  }

  getMultipleChoiceChartData(body: any) {
    return this.http.post<{} | any[]>(this.url + `v5/getMultipleChoiceChartData`, body);
  }

  getListVerbatim(body: any) {
    return this.http.post<{} | any[]>(this.url + `v1/getListVerbatim`, body);
  }
  getYesResponseTrend(body: any) {
    return this.http.post<{} | any[]>(this.url + `v3/getYesResponseTrend`, body);
  }
  getQuestionSemiDonutChart(body: any) {
    const urlQuestionSemiDonut: string = "v5/getQuestionSemiDonutChartElastic";

    return this.http.post<{} | any[]>(this.url + urlQuestionSemiDonut, body);
  }
  getFieldworkReportSummary(body: IPayload) {
    let path;
    if (this.router.url != null && this.router.url.includes('elastic')) {
      path = 'v5/getFieldworkSummary';
    } else {
      path = 'v3/getFieldworkReportSummary';
    }
    return this.http
      .post<FieldworkReportSummary>(this.url + path, body)
      .pipe(
        map((res) => new FieldworkReportSummary(res)),
        catchError((error) => of(new FieldworkReportSummary()))
      );
  }
  getMainKPIAllEntity(body: any) {
    return this.http.post<{} | any[]>(this.url + `v1/getMainKPIAllEntity`, body);
  }

  getDataQualityPanel(payload: IPayload, admittedKeys?: (keyof DataQualityResponse)[]) {
    let path;
    if (this.router.url != null && this.router.url.includes('elastic')) {
      return this.getFieldworkDataQuality(payload, admittedKeys);
    } else {
      return this.getDataQualityPanelV3(payload, admittedKeys);
    }
  }
  getFieldworkDataQuality(payload: IPayload, admittedKeys?: (keyof DataQualityResponse)[]) {
    payload.extraFilters = { ...payload.extraFilters, FlagUnique: this.flagUniqueService.flagUnique };
    if (payload.extraFilters.FlagUnique === 'false') {
      delete payload.extraFilters.FlagUnique;
    }
    return this.makeDataQualityHttpCall(payload, admittedKeys, 'fieldwork/v2/getFieldworkDataQuality');
  }
  getDataQualityPanelV3(payload: IPayload, admittedKeys?: (keyof DataQualityResponse)[]) {
    return this.makeDataQualityHttpCall(payload, admittedKeys, 'v3/getDataQualityPanel');
  }
  private makeDataQualityHttpCall(payload: IPayload, admittedKeys?: (keyof DataQualityResponse)[], path?: string) {
    payload.question.locale = this.applicationService.languageSelectValue
    return this.http.post<DataQualityResponse>(this.url + path, payload).pipe(
      map((res) => this.mapDataQualityData(res, admittedKeys)),
      catchError((err) => {
        console.error(err);
        return of([]);
      })
    );
  }

  getNpsRankingByQuartile(filters: IPayload) {
    return this.http
      .post<null | NpsTrendByQuartile>(this.url + `v3/getRankingByQuartileNPS`, filters)
      .pipe(
        map((res) => (res && res.dealer === "NN" ? null : res)),
        catchError((err) => of(null))
      );
  }

  getFollowUpRankingByQuartile(filters: IPayload) {
    return this.http
      .post<null | NpsTrendByQuartile>(this.url + `fieldwork/v3/getRankingByQuartileFollowUp`, filters)
      .pipe(
        map((res) => (res && res.dealer === "NN" ? null : res)),
        catchError((err) => of(null))
      );
  }

  getRejectReason(payload: IPayload) {
    let path;
    if (this.router.url != null && this.router.url.includes('elastic')) {
      path = 'v5/getFieldworkRejectedReasons';
    } else {
      path = 'v3/getRejectReason';
    }
    payload.question = {
      limit: "",
      locale: this.applicationService.languageSelectValue,
      orderByScore: "",
      questionCode: "",
      section: this.applicationService.navBarSelectValue.idItem,
      tab: this.applicationService.navBarSelectValue.idItem,
    };
   
    return this.http.post<RejectReason[]>(this.url + path, payload).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      })
    );
  }

  private mapDataQualityData(
    data: DataQualityResponse,
    admittedKeys?: (keyof DataQualityResponse)[]
  ): IactionTitleChart[] {
    let value: number;
    if (
      data.eligible &&
      data.imported &&
      data.eligible.value &&
      data.imported.value &&
      Number(data.eligible.value) &&
      Number(data.imported.value)
    ) {
      value = (Number(data.eligible.value) / Number(data.imported.value)) * 100;
    } else {
      value = 0;
    }
    let previous: number;
    if (
      data.eligible &&
      data.imported &&
      data.eligible.previous &&
      data.imported.previous &&
      Number(data.eligible.previous) &&
      Number(data.imported.previous)
    ) {
      previous = (Number(data.eligible.previous) / Number(data.imported.previous)) * 100;
    } else {
      previous = 0;
    }
    let entries = Object.entries(data) as [keyof DataQualityResponse, DataQualityInfo][];
    let mappedResponse = entries
      .filter(([key]) => (admittedKeys ? admittedKeys.includes(key) : true))
      .sort(([keyA], [keyB]) => {
        if (!admittedKeys) return 0;
        return admittedKeys?.indexOf(keyA) > admittedKeys?.indexOf(keyB) ? 1 : -1;
      })
      .map(([key, info]) => {
        let action: IactionTitleChart = {
          title: "Data_" + key,
          value: (info ? info.value : 0) + (info?.valueType === "percentage" ? "%" : ""),
          percentMonth: info ? info.previous : "0",
        };
        return action;
      });

    return mappedResponse;
  }

  getBestCategoriesNPS(payload: IPayload) {
    return this.http.post<CategoriesNps>(this.url + `v4/getBestCategoriesNPS`, payload)
      .pipe(
        map((res) => {

          res.bestCategories.sort((a, b) => (a.position > b.position ? 1 : -1));
          res.wrostCategories.sort((a, b) => (a.position > b.position ? 1 : -1));
          let positiveData = res.bestCategories.map((category) => category.occurrenceCount);
          let negativeData = res.wrostCategories.map((category) => category.occurrenceCount);
          let positiveTooltips = res.bestCategories.map(
            (category) => `(${category.occurrencePerc} %)`
          );
          let negativeTooltips = res.wrostCategories.map(
            (category) => `(${category.occurrencePerc} %)`
          );
          let bLabels = res.bestCategories.map((category) => category.category);
          let wLabels = res.wrostCategories.map((category) => category.category);
          let scoreData = {
            positive: [
              {
                data: positiveData,
                labels: bLabels,
                tooltips: positiveTooltips,
                borderColor: "#30D988",
                backgroundColor: "#30D988",
                total: res.positiveOccurrenceCount,
              },
            ],
            negative: [
              {
                data: negativeData,
                labels: wLabels,
                tooltips: negativeTooltips,
                borderColor: "#BA2A2A",
                backgroundColor: "#BA2A2A",
                total: res.negativeOccurrenceCount,
              },
            ],
          };
          return scoreData;
        })
      );
  }

  getTopCommentCategoriesNPS(payload: IPayload) {
    let mock: CommentCategory[] = [
      {
        category: "test2",
        position: 2,
        data: {
          positiveOccurrencePerc: 40,
          negativeOccurrencePerc: 30,
          impact: "10",
        },
      },
      {
        category: "test1",
        position: 1,
        data: {
          positiveOccurrencePerc: 50,
          negativeOccurrencePerc: 10,
          impact: "10",
        },
      },
      {
        category: "test3",
        position: 1,
        data: {
          positiveOccurrencePerc: 100,
          negativeOccurrencePerc: 90,
          impact: "10",
        },
      },
    ];
    return (
      this.http
        .post<CommentCategory[]>(this.url + `v4/getTopCommentCategoriesNPS`, payload)
        //return of(mock)
        .pipe(
          map((res) =>
            res
              .sort((a, b) => (a.position > b.position ? 1 : -1))
              .map((el) => ({
                category: el.category,
                positive: el.data.positiveOccurrencePerc,
                negative: el.data.negativeOccurrencePerc,
              }))
          )
        )
    );
  }

  getNpsBrandingComparison(payload: IPayload) {
    return this.http.post<any>(this.url + `v3/getNPSBrandQuestionComparison`, payload).pipe(
      catchError((err) => {
        return of({});
      })
    );
  }
  getBrandQuestionComparison(payload: IPayload) {
    let urlQuestionComparison: string;
    if (this.applicationService.navBarSelectValue?.displayName === "B2E") {
      urlQuestionComparison = "v3/getBrandQuestionComparison";
    } else {
      urlQuestionComparison = "v5/getBrandQuestionComparison";
    }
    return this.http.post<any>(this.url + urlQuestionComparison, payload).pipe(
      catchError((err) => {
        return of({});
      })
    );
  }

  getBrandQuestionComparisonOnlineReputation(payload: IPayload) {
    // return of({
    //   errors: null,
    //   viewType: "REGION",
    //   data: {
    //     titles: [
    //       "TOTAL",
    //       "stellantis-service_a_DP3:title",
    //       "stellantis-service_a_Q10_m:title",
    //       "stellantis-service_a_Q1_s:title",
    //       "stellantis-service_NPS_Dealer:title",
    //       "Detractors",
    //     ],
    //     columns: [
    //       "TOTAL",
    //       "stellantis-service_a_DP3",
    //       "stellantis-service_a_Q10_m",
    //       "stellantis-service_a_Q1_s",
    //       "stellantis-service_NPS_Dealer",
    //       "stellantis-service_NPS_Dealer-Detractors",
    //     ],
    //     rows: ["multiBrand", "b_CH", "b_AR", "b_JP"],
    //     values: [
    //       [19887, 780, 483, 18624],
    //       [95.07, 93.46, 91.93, 95.22],
    //       [40.95, 38.3, 65.98, 40.52],
    //       [63.6, 60.0, 83.85, 63.22],
    //       [60.34, 52.31, 82.61, 60.1],
    //       [11.07, 15.38, 4.348, 11.07],
    //     ],
    //   },
    // });


    return this.http.post<any>(this.url + `v1/getBrandCompetitors`, payload).pipe(
      catchError((err) => {
        return of({});
      })
    );
  }
}

interface CategoriesNps {
  bestCategories: Category[];
  negativeOccurrenceCount: number;
  positiveOccurrenceCount: number;
  wrostCategories: Category[];
}

interface Category {
  category: string;
  position: number;
  occurrenceCount: number;
  occurrencePerc: number;
}
